import { useQuery } from 'react-query'

import { SAFE_CAPABILITIES, fetchSchemaCapabilities } from '../api/capabilities'

export const SCHEMA_CAPABILITIES_QUERY_KEY = ['schema-capabilities']

export const SCHEMA_STATUS = {
  LOADING: 'loading',
  READY: 'ready',
  FAILED: 'failed'
}

// Wiederholungen der Probe bei Netzfehlern, 0,5, 1 und 2 Sekunden Abstand
// (react-query zählt failureCount ab 0).
// Als Objekt exportiert, damit Tests die Wartezeit abschalten können.
export const SCHEMA_PROBE_RETRY = {
  count: 3,
  delay: (failureCount) => Math.min(1000 * 2 ** (failureCount - 1), 8000)
}

// Nur ein Erfolg bleibt für die Session (staleTime Infinity gilt nur für
// Daten). Ein Fehler hat keine Daten, deshalb läuft die Probe beim nächsten
// Mount oder per refetch neu.
export const schemaCapabilitiesQueryOptions = () => ({
  queryKey: SCHEMA_CAPABILITIES_QUERY_KEY,
  queryFn: () => fetchSchemaCapabilities(),
  staleTime: Infinity,
  cacheTime: Infinity,
  retry: (failureCount) => failureCount < SCHEMA_PROBE_RETRY.count,
  retryDelay: (failureCount) => SCHEMA_PROBE_RETRY.delay(failureCount),
  refetchOnWindowFocus: false,
  // Ein Reconnect würde einen Fehlschlag still neu prüfen, nur der Button
  // „Erneut prüfen" soll das tun
  refetchOnReconnect: false
})

/**
 * Status aus dem Query-Stand. Ein Refetch ohne Daten setzt react-query v3
 * zurück auf loading und leert error. errorUpdatedAt bleibt aber stehen, so
 * bleibt ein Fehlschlag FAILED, bis die Probe wirklich klappt. Sonst würde
 * „Erneut prüfen" die Seiten in den Loader schicken und Formulare aushängen.
 */
export const schemaStatusOf = ({ data, isError, errorUpdatedAt }) => {
  if (data) return SCHEMA_STATUS.READY
  if (isError || errorUpdatedAt > 0) return SCHEMA_STATUS.FAILED
  return SCHEMA_STATUS.LOADING
}

/**
 * Was das Schema dieser Stage kennt.
 * - status loading, die Probe läuft (auch während der Wiederholungen).
 *   Paket-Bereiche sollten bis dahin nicht rendern, sonst springt die Seite.
 * - status ready, eindeutiges Ergebnis. paymentPlan sagt, ob plan existiert.
 * - status failed, keine eindeutige Antwort. paymentPlan ist false, Pakete
 *   und Payment-Schreiben bleiben gesperrt, bis refetch gelingt. Bleibt auch
 *   während eines neuen Versuchs failed (isFetching zeigt ihn an).
 *
 * @returns {{ paymentPlan: boolean, products: string[]|null,
 *   source: 'introspection'|'probe'|'failed', status: string,
 *   confirmed: boolean, isLoading: boolean, isError: boolean,
 *   isFetching: boolean, refetch: () => Promise<unknown> }}
 */
export const useSchemaCapabilities = () => {
  const { data, isError, errorUpdatedAt, isFetching, refetch } = useQuery(
    schemaCapabilitiesQueryOptions()
  )
  const capabilities = data || SAFE_CAPABILITIES
  const status = schemaStatusOf({ data, isError, errorUpdatedAt })

  return {
    paymentPlan: capabilities.paymentPlan === true,
    products: Array.isArray(capabilities.products)
      ? capabilities.products
      : null,
    source: data ? capabilities.source : SAFE_CAPABILITIES.source,
    status,
    confirmed: status === SCHEMA_STATUS.READY,
    isLoading: status === SCHEMA_STATUS.LOADING,
    isError: status === SCHEMA_STATUS.FAILED,
    isFetching,
    refetch
  }
}
